import { Fragment, type ReactNode } from 'react';
import s from './Assistant.module.css';

/**
 * A deliberately small Markdown subset for assistant answers, rendered as React elements (no
 * HTML injection, RULES §SEC-4): paragraphs, bullet and numbered lists, **bold**, `code`, and
 * pipe tables shown as monospace blocks.
 */
export function RichText({ text }: { text: string }) {
  const blocks = text.replace(/\r/g, '').split(/\n{2,}/);
  return (
    <div className={s.rich}>
      {blocks.map((block, i) => (
        <Block key={i} block={block} />
      ))}
    </div>
  );
}

function Block({ block }: { block: string }) {
  const lines = block.split('\n').filter((l) => l.trim() !== '');
  if (!lines.length) return null;
  if (lines.every((l) => l.trim().startsWith('|'))) {
    return <pre className={s.table}>{lines.filter((l) => !/^\s*\|?\s*:?-{2,}/.test(l)).join('\n')}</pre>;
  }
  if (lines.every((l) => /^\s*[-*•]\s+/.test(l))) {
    return (
      <ul>
        {lines.map((l, i) => (
          <li key={i}>{inline(l.replace(/^\s*[-*•]\s+/, ''))}</li>
        ))}
      </ul>
    );
  }
  if (lines.every((l) => /^\s*\d+[.)]\s+/.test(l))) {
    return (
      <ol>
        {lines.map((l, i) => (
          <li key={i}>{inline(l.replace(/^\s*\d+[.)]\s+/, ''))}</li>
        ))}
      </ol>
    );
  }
  const heading = /^#{1,4}\s+(.*)$/.exec(lines[0] ?? '');
  if (heading && lines.length === 1) return <p className={s.richHeading}>{inline(heading[1] ?? '')}</p>;
  return (
    <p>
      {lines.map((l, i) => (
        <Fragment key={i}>
          {i > 0 && <br />}
          {inline(l)}
        </Fragment>
      ))}
    </p>
  );
}

function inline(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  const re = /(\*\*[^*]+\*\*|`[^`]+`)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let k = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const token = m[0];
    out.push(token.startsWith('**') ? <strong key={k++}>{token.slice(2, -2)}</strong> : <code key={k++}>{token.slice(1, -1)}</code>);
    last = m.index + token.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}
